import { AlertTriangleIcon, CheckIcon } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'
import { Link } from 'react-router'

import { PageBody, PageHeader } from '@/components/AppShell'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useStaff } from './hooks'
import { staffErrorText } from './shared'
import { MAX_REASON, MIN_REASON, reasonProblem, type Capability } from './tier'

/** Shown instead of a page when the staff session lacks the second factor (S11). */
export function MfaRequired() {
  return (
    <>
      <PageHeader eyebrow="Staff" title="Second factor required" />
      <PageBody>
        <Alert variant="warning" data-testid="staff-mfa-required">
          <AlertTriangleIcon />
          <AlertTitle>Sign in with a second factor</AlertTitle>
          <AlertDescription>
            <p>
              The staff console only works for a session that used a second factor. This session
              signed in with a password alone, so every staff page would be refused. Sign out, then
              sign in again and complete the one-time-code step. If your account has no second factor
              yet, enrol one in your Keycloak account settings first.
            </p>
          </AlertDescription>
        </Alert>
      </PageBody>
    </>
  )
}

/**
 * Wraps a staff page: refuses a non-staff caller, shows the MFA page when the session lacks the
 * second factor, and refuses a tier below `needs` (the server refuses it anyway).
 */
export function StaffGate({ needs, children }: { needs?: Capability; children: ReactNode }) {
  const staff = useStaff()
  if (staff.loading) return null
  if (staff.tier === null) {
    return (
      <>
        <PageHeader eyebrow="Staff" title="Not available" />
        <PageBody>
          <Alert variant="destructive" data-testid="staff-forbidden">
            <AlertTitle>403 — this page is for saltseed staff</AlertTitle>
            <AlertDescription>Your account does not hold a staff role.</AlertDescription>
          </Alert>
        </PageBody>
      </>
    )
  }
  if (!staff.mfa) return <MfaRequired />
  if (needs !== undefined && !staff.can(needs)) {
    return (
      <>
        <PageHeader eyebrow="Staff" title="Not available" />
        <PageBody>
          <Alert variant="destructive" data-testid="staff-tier-forbidden">
            <AlertTitle>Your tier cannot open this page</AlertTitle>
            <AlertDescription>
              It needs a higher staff tier than <span className="font-mono">{staff.tier}</span>.
            </AlertDescription>
          </Alert>
        </PageBody>
      </>
    )
  }
  return <>{children}</>
}

/** Tone for the state words the API uses. */
export function StateBadge({ state }: { state: string | null | undefined }) {
  if (state === null || state === undefined) return <span className="text-muted-foreground">—</span>
  const lower = state.toLowerCase()
  const variant =
    lower === 'active' || lower === 'completed' || lower === 'done'
      ? 'success'
      : lower === 'suspended' || lower === 'deactivated' || lower === 'failed' || lower === 'paused'
        ? 'destructive'
        : lower === 'pending' || lower === 'running'
          ? 'default'
          : 'secondary'
  return <Badge variant={variant}>{lower}</Badge>
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm">{children}</dd>
    </div>
  )
}

export function StaffLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-primary underline-offset-2 hover:underline">
      {children}
    </Link>
  )
}

/** The result line after a write: whether the account's ADMINs were told (S10). */
export function ActionNotice({
  notice,
  onDismiss,
}: {
  notice: { title: string; notified: boolean | undefined; detail?: ReactNode } | null
  onDismiss: () => void
}) {
  if (notice === null) return null
  return (
    <Alert variant="info" data-testid="staff-action-done" className="items-center">
      <CheckIcon />
      <AlertTitle>{notice.title}</AlertTitle>
      <AlertDescription>
        <div className="flex items-center justify-between gap-3">
          <span>
            {notice.notified === undefined
              ? 'Recorded in the audit log.'
              : notice.notified
                ? 'Recorded in the audit log. The account’s administrators were emailed.'
                : 'Recorded in the audit log. The notification email could not be sent.'}
          </span>
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
        {notice.detail}
      </AlertDescription>
    </Alert>
  )
}

export interface ReasonDialogProps {
  title: string
  /** The confirmation copy: what will happen and to whom. */
  description: ReactNode
  confirmLabel: string
  destructive?: boolean
  minLength?: number
  /** Fields above the reason (an amount, a plan). */
  children?: ReactNode
  /** False blocks the confirm button (the extra fields are invalid). */
  extraValid?: boolean
  onClose: () => void
  /** Runs the write. A rejection stays in the dialog. */
  onConfirm: (reason: string) => Promise<unknown>
  testId?: string
}

/**
 * Every staff write goes through this: a required reason (the same minimum the server enforces),
 * the confirmation copy, and the server's refusal shown in place when it comes back.
 */
export function ReasonDialog({
  title,
  description,
  confirmLabel,
  destructive = false,
  minLength = MIN_REASON,
  children,
  extraValid = true,
  onClose,
  onConfirm,
  testId = 'staff-reason-dialog',
}: ReasonDialogProps) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<unknown>(undefined)
  const [busy, setBusy] = useState(false)
  const id = useId()
  const problem = reasonProblem(reason, minLength)
  const ready = problem === null && extraValid && !busy

  async function submit() {
    if (!ready) return
    setBusy(true)
    setError(undefined)
    try {
      await onConfirm(reason.trim())
      onClose()
    } catch (caught) {
      setError(caught)
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose()
      }}
    >
      <DialogContent data-testid={testId}>
        <form
          className="grid gap-5"
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription asChild>
              <div>{description}</div>
            </DialogDescription>
          </DialogHeader>

          {children}

          <div className="space-y-1.5">
            <Label htmlFor={id}>Reason (kept in the audit log)</Label>
            <Textarea
              id={id}
              value={reason}
              maxLength={MAX_REASON}
              aria-invalid={reason.length > 0 && problem !== null}
              onChange={(event) => {
                setReason(event.target.value)
              }}
            />
            <p className="text-xs text-muted-foreground">
              {problem ?? 'Ready.'}
            </p>
          </div>

          {error === undefined ? null : (
            <Alert variant="destructive" data-testid="staff-action-error">
              <AlertTriangleIcon />
              <AlertDescription>{staffErrorText(error)}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant={destructive ? 'destructive' : 'default'} disabled={!ready}>
              {busy ? 'Working…' : confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** A short "Load more" row for keyset lists. */
export function LoadMore({
  shown,
  hasNext,
  loading,
  onMore,
}: {
  shown: number
  hasNext: boolean
  loading: boolean
  onMore: () => void
}) {
  return (
    <div className="flex items-center gap-3">
      <p className="text-xs text-muted-foreground">{shown} shown</p>
      {hasNext ? (
        <Button variant="outline" size="sm" disabled={loading} onClick={onMore}>
          {loading ? 'Loading…' : 'Load more'}
        </Button>
      ) : null}
    </div>
  )
}

/** Compact id for tables: first 8 characters, full id on hover. */
export function ShortId({ id }: { id: string | null | undefined }) {
  if (id === null || id === undefined) return <span className="text-muted-foreground">—</span>
  return (
    <span className="font-mono text-xs" title={id}>
      {id.slice(0, 8)}
    </span>
  )
}
